import { z } from 'zod';

/**
 * Konfiguracja czytana w RUNTIME z /config.json, nie wkompilowana w bundle.
 *
 * Powód: jeden obraz Dockera ma działać na dev, test i produkcji, a adres
 * serwera eKuria (Keycloak), realm i client_id różnią się między środowiskami.
 * Plik montuje się z ConfigMapa/woluminu obok statyków nginxa.
 */
const oidcSchema = z.object({
  /** Pełny adres realmu, np. https://ekuria.example.org/realms/iskk */
  authority: z.url(),
  clientId: z.string().min(1),
  scope: z.string().default('openid profile email'),
  /** Ścieżka, na którą Keycloak odsyła po zalogowaniu (musi być w Valid redirect URIs). */
  redirectPath: z.string().startsWith('/').default('/auth/callback'),
  postLogoutPath: z.string().startsWith('/').default('/'),
  /**
   * Nazwa dostawcy w formie, jaka pasuje po „Zaloguj przez …” (biernik).
   * Dla polskiego to „eKurię”, nie „eKuria”.
   */
  providerName: z.string().default('eKurię'),
});

const featuresSchema = z.object({
  oidcLogin: z.boolean().default(true),
  tokenLogin: z.boolean().default(true),
  /** Mocki MSW zamiast prawdziwego API. Działa wyłącznie w trybie dev. */
  useMocks: z.boolean().default(false),
});

export const appConfigSchema = z.object({
  apiBaseUrl: z.string().default('/api'),
  /**
   * Bazowy URL backendu dashboardu (Spring Boot). Używany do wywołań
   * diagnostycznych endpointów integracyjnych typu /api/test/open i /api/test/secured.
   */
  dashboardApiUrl: z.url().default('http://localhost:8080'),
  oidc: oidcSchema,
  features: featuresSchema.prefault({}),
});

export type AppConfig = z.infer<typeof appConfigSchema>;
export type OidcConfig = z.infer<typeof oidcSchema>;

let cached: AppConfig | null = null;

/**
 * Ładuje i waliduje konfigurację. Wywoływana raz, przed createRoot().
 * Błąd tutaj jest błędem krytycznym — aplikacja nie ma jak działać bez adresu eKurii.
 */
export async function loadAppConfig(): Promise<AppConfig> {
  if (cached) return cached;

  const response = await fetch('/config.json', { cache: 'no-store' });
  if (!response.ok) {
    throw new Error(
      `Nie udało się wczytać /config.json (HTTP ${String(response.status)}).`,
    );
  }

  const parsed = appConfigSchema.safeParse(await response.json());
  if (!parsed.success) {
    throw new Error(
      `Plik /config.json jest niepoprawny:\n${z.prettifyError(parsed.error)}`,
    );
  }

  cached = parsed.data;
  return cached;
}

/** Konfiguracja po załadowaniu. Rzuca, jeśli wywołana przed loadAppConfig(). */
export function getAppConfig(): AppConfig {
  if (!cached) {
    throw new Error('getAppConfig() wywołane przed loadAppConfig().');
  }
  return cached;
}

/** Czy mocki MSW mają wystartować. Nigdy na produkcji, niezależnie od config.json. */
export function shouldUseMocks(config: AppConfig): boolean {
  return import.meta.env.DEV && config.features.useMocks;
}
